import { useTranslation } from 'react-i18next'
import MilestoneProgressBar from './MilestoneProgressBar'
import { type Milestone } from './milestoneHelpers'

import { useContributions } from '@hooks/index'
import { calculateDaysRemaining } from '@/util/helpers'

export default function ContributionProgressBar() {
  const { t } = useTranslation()
  const { data } = useContributions()

  let goal = Number.parseFloat(data?.contributionGoal ?? '0')
  let amount = data?.contributions?.reduce((acc, contribution) => acc + Number(contribution.amount), 0) ?? 0
  let max = goal
  let milestones: Milestone[] = [
    {
      position: goal,
      variant: 'books',
    },
  ]

  if (import.meta.env.VITE_NEW_GOAL_STRATEGY === 'true') {
    // fallback to old goal, amount, and milestones if new goal strategy is not populated
    const target = data?.goals.contributionGoal.target ?? goal
    amount = data?.goals.contributionGoal.progress ?? amount
    goal =
      data?.goals.contributionGoal.goals.find((g: number) => g > amount) ??
      data?.goals.contributionGoal.goals[data?.goals.contributionGoal.goals.length - 1] ??
      goal
    max = data?.goals.contributionGoal.goals[data?.goals.contributionGoal.goals.length - 1] ?? goal
    const milestoneCount = data?.goals.contributionGoal.goals.length ?? 0

    milestones =
      data?.goals.contributionGoal.goals.map((g: number, index: number) => {
        if (g === target) {
          return {
            position: g,
            variant: 'books',
          }
        } else if (milestoneCount >= 3 && index + 1 === milestoneCount) {
          return {
            position: g,
            variant: 'medal',
            color: 'green',
          }
        }
        /**
          * Gold, silver, and bronze UX treatment removed in favor of all icons being green
        else if (milestoneCount >= 3) {
          
          // use gold, silver, bronze medals
          if (index + 1 === milestoneCount) {
            return {
              position: g,
              variant: 'medal',
              color: 'gold',
            }
          } else if (index + 2 === milestoneCount) {
            return {
              position: g,
              variant: 'medal',
              color: 'silver',
            }
          } else if (index + 3 === milestoneCount) {
            return {
              position: g,
              variant: 'medal',
              color: 'bronze',
            }
          }
        }
        */

        // fallback: use party popper for all other milestones
        return {
          position: g,
          variant: 'goal',
        }
      }) ?? milestones
  }

  const disableDate = data?.disableDate ?? null
  const contributorCount = data?.contributions.length ?? 0
  const daysRemaining = calculateDaysRemaining(disableDate)

  return (
    <div id='contribution-progress-bar' className='text-center'>
      <div className='text-cta mb-6 text-xl font-serif '>{t('content.components.contribution_progress.subtitle')}</div>

      <div className='flex items-end justify-between gap-4'>
        <div className='flex items-baseline gap-2'>
          <span className='text-cta text-2xl font-serif font-bold'>${amount}</span>
          <span className='text-sm text-secondary-txt'>
            {t('content.components.contribution_progress.current_progress_text')}
          </span>
        </div>
        {goal > amount ? (
          <div className='flex items-baseline gap-2 text-right'>
            <span className='text-cta text-2xl font-serif font-bold'>${goal - amount}</span>
            <span className='text-sm text-secondary-txt'>
              {t('content.components.contribution_progress.goal_text')}
            </span>
          </div>
        ) : (
          <div className='flex items-baseline gap-2 text-right'>
            <span className='text-cta text-2xl font-serif font-bold'>
              {t('content.components.contribution_progress.goal_achieved')}
            </span>
          </div>
        )}
      </div>

      <MilestoneProgressBar value={amount} max={max} milestones={milestones} />

      <div className='pt-6 flex items-end justify-between gap-4'>
        <div className='flex items-baseline gap-2'>
          <span className='text-cta text-2xl font-serif font-bold'>{contributorCount}</span>
          <span className='text-sm text-secondary-txt'>
            {t('content.components.contribution_progress.contributor_count', { count: contributorCount })}
          </span>
        </div>
        <div className='flex items-baseline gap-2 text-right'>
          <span className='text-cta text-2xl font-serif font-bold'>{daysRemaining}</span>
          <span className='text-sm text-secondary-txt'>
            {t('content.components.contribution_progress.countdown', { count: daysRemaining })}
          </span>
        </div>
      </div>
    </div>
  )
}
